import React from "react";
import { useMutation } from "react-query";
import { DEFAULT_PER_PAGE } from "../../../../helpers/default";
import ContentBlock from "../../../../components/Layout/ContentBlock";
import FilterMenu from "../../../../components/Layout/FilterMenu";
import {
  ButtonSecondary,
  Pagination,
  TextInput,
} from "../../../../components/globals";
import TableFilterBox from "../../../../components/common/TableFilterBox";
import {
  ButtonDelete,
  ButtonEditRow,
  ButtonReloadRow,
} from "../../../../components/common/Buttons";
import {
  antdAsyncMessageError,
  antdAsyncMessageSuccess,
} from "../../../../helpers/AsyncMessage";
import { Modal } from "antd";
import { useModal } from "../../../../hooks";
import { InputSwitchComponent } from "../../../../components/globals";
import { IconXmlFeedGenerationBanner } from "../../../../components/Layout/Icons";
import SwitchComp from "../../../../components/common/SwitchComp";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useQuery } from "react-query";
import axios from "axios";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import XMLFeedGenerationTabUi from "./XMLFeedGenerationTabUi";
import ContentValidation from "../../../../components/common/ContentValidation";
import ModalXMLFeedGeneration from "./modals/ModalXMLFeedGeneration";
import { FaRegCopy } from "react-icons/fa";


dayjs.extend(isBetween);

const { confirm } = Modal;

const Feeds = ({ tabId, setTabId, onSupplierNameChange }) => {
  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(DEFAULT_PER_PAGE);
  const [totals, setTotals] = React.useState(0);
  const [filterTitle, setFilterTitle] = React.useState("");
  const [filterUrl, setFilterUrl] = React.useState("");
  const [filterStatus, setFilterStatus] = React.useState("");

  const { modalOpt, setModalOpt, closeModal } = useModal();

  const fetchFeeds = async () => {
    let url = `/api/v1/feeds?`;
    if (filterTitle) {
      url += `&q[title_i_cont]=${filterTitle}`;
    }
    if (filterUrl) {
      url += `&q[url_i_cont]=${filterUrl}`;
    }
    if (filterStatus === "true") {
      url += `&q[active_true]=true`;
    }
    if (filterStatus === "false") {
      url += `&q[active_false]=true`;
    }
    const response = await axios.get(url);
    return response.data;
  };

  const {
    data: feeds,
    isLoading: feedsLoading,
    error: feedsError,
    refetch: refetchFeeds,
  } = useQuery("feeds", fetchFeeds);

  const handleFilterChange = () => {
    refetchFeeds();
  };

  const deleteBannerMutation = useMutation(
    (id) => axios.delete(`/api/v1/feeds/${id}`),
    {
      onSuccess: () => {
        antdAsyncMessageSuccess({
          text: "Successfully deleted",
          key: "delete_feed",
        });
        refetchFeeds();
      },
      onError: () => {
        antdAsyncMessageError({
          text: "Error occurred",
          key: "delete_feed",
        });
      },
    }
  );

  const copyToClipboard = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        antdAsyncMessageSuccess({ text: "URL copied to clipboard!", key: "copy-url" });
      }).catch(err => {
        antdAsyncMessageError({ text: "Failed to copy URL: " + err, key: "copy-url-error" });
      });
    } else {
      antdAsyncMessageError({ text: "Clipboard API not available in this browser.", key: "copy-url-error" });
    }
  };

  const handleFilterClear = () => {
    setFilterTitle("");
    setFilterUrl("");
    setFilterStatus("");
  };


  const mutationPUT = useMutation({
    mutationFn: (feed) => {
      return axios.put(`/api/v1/feeds/${feed.id}`, {
        feed: {
          active: feed.status,
        },
      }).then((res) => res.data);
    },
    onSuccess: async () => {
      refetchFeeds();
      antdAsyncMessageSuccess({ text: "Successfully updated", key: "default" });
    },
    onError: antdAsyncMessageError,
  });

  React.useEffect(() => {
    if (feeds?.total) {
      setTotals(feeds?.total);
    }
  }, [feeds]);

  React.useEffect(() => {
    refetchFeeds();
  }, [modalOpt]);

  return (
    <>
      <ContentBlock>
        <FilterMenu
          length={feeds?.data?.length}
          text2={`XML feeds`}
          iconComponent={<IconXmlFeedGenerationBanner />}
          onChangeCountPage={setPerPage}
          countPage={perPage}
          page={page}
          totals={feeds?.data?.totals}
        />
      </ContentBlock>
      <div className="col-12">
        <XMLFeedGenerationTabUi tabId={tabId} setTabId={setTabId} />
      </div>
      <ContentBlock>
        <TableFilterBox
          filter={true}
          onFilter={() => {
            handleFilterChange();
          }}
          add={false}
          clearFilter={() => handleFilterClear()}
          button1={
            <>
              <div className="float-left ml-2" style={{ width: 104 }}>
                <ButtonSecondary
                  onClick={() => setModalOpt("new_xml_feed", 0)}
                >
                  New XML feed
                </ButtonSecondary>
              </div>
            </>
          }
        >
          <div className="d-flex justify-content-between flex-1 flex-wrap">
            <div className="d-flex flex-wrap">
              <>
                <div className="px-1">
                  <TextInput
                    text="Title"
                    name="Title"
                    value={filterTitle}
                    onChange={(val) => {
                      setFilterTitle(val.target.value);
                    }}
                  />
                </div>
                <div className="px-1">
                  <TextInput
                    text="URL"
                    name="url"
                    value={filterUrl}
                    onChange={(val) => {
                      setFilterUrl(val.target.value);
                    }}
                  />
                </div>
                <div className="px-1">
                  <InputSwitchComponent
                    label="Status"
                    name="auto_type"
                    options={[
                      {
                        label: "All",
                        value: "",
                      },
                      {
                        label: "Active",
                        value: "true",
                      },
                      {
                        label: "Inactive",
                        value: "false",
                      },
                    ]}
                    value={filterStatus}
                    onChange={(val) => {
                      setFilterStatus(val.target.value);
                    }}
                  />
                </div>
              </>
            </div>
          </div>
        </TableFilterBox>
        <div>
          <div className="table-responsive" style={{ overflow: "hidden" }}>
            <ContentValidation
              loader={feedsLoading}
              array={feeds?.data}
              error={feedsError}
            >
              <table className="table mt-3">
                <thead>
                  <tr className="text-muted">
                    <th className="text-black">Title</th>
                    <th className="text-black">URL</th>
                    <th className="text-black">Status</th>
                    <th className="text-black">Full generation date</th>
                    <th className="text-black">Priority generation date</th>
                    <th className="text-black"></th>
                  </tr>
                </thead>
                <tbody>
                  {feeds?.data?.map?.((item) => (
                    <tr className="align-middle tableColumn" key={item.id}>
                      <td className="align-middle">{item?.title}</td>
                      <td className="align-middle float-left">
                        {item?.url && (
                          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 5 }}>
                            {"feeds.bgbx.eu/" + item?.url} <FaRegCopy color="#CACACA" className="cursor-pointer" onClick={() => copyToClipboard("https://feeds.bgbx.eu/" + item?.url)} />
                          </div>
                        )}
                      </td>
                      <td className="align-middle">
                        <div
                          className="d-flex align-items-center switch-container"
                        >
                          <SwitchComp
                            className=""
                            checked={item.active}
                            onChange={() => {
                              mutationPUT.mutate({ id: item.id, status: !item.active });
                            }}
                          />
                        </div>
                      </td>
                      <td className="align-middle">
                        {item?.generated_at ? dayjs(item.generated_at).format("YYYY-MM-DD HH:mm") : ""}
                      </td>
                      <td className="align-middle">
                        {item?.priority_generated_at ? dayjs(item.priority_generated_at).format("YYYY-MM-DD HH:mm") : ""}
                      </td>
                      <td className="align-middle">
                        <div className="d-flex justify-content-end gap-5">
                          <ButtonReloadRow
                            className="d-flex justify-content-center align-items-center"
                            onClick={() => {
                              confirm({
                                title: "Are you sure?",
                                icon: <ExclamationCircleOutlined className="text-primary" />,
                                content: "This will start feed generation.",
                                onOk: async () => {
                                  try {
                                    await axios.post(`/api/v1/feeds/${item.id}/generate`);
                                    antdAsyncMessageSuccess({ text: "Feed generation started", key: "reload_feed" });
                                  } catch (err) {
                                    antdAsyncMessageError({ text: "Failed to start feed generation", key: "reload_feed" });
                                  }
                                },
                                onCancel() {
                                  console.log("Cancel");
                                },
                              });
                            }}
                          />
                          <ButtonEditRow
                            className="d-flex justify-content-center align-items-center"
                            onClick={() =>
                              setModalOpt("edit_xml_feed", item.id)
                            }
                          />
                          <ButtonDelete
                            className="d-flex justify-content-center align-items-center"
                            onDelete={() =>
                              confirm({
                                title: "Are you sure?",
                                icon: (
                                  <ExclamationCircleOutlined className="text-primary" />
                                ),
                                content:
                                  "You will not be able to undo this action",
                                onOk() {
                                  deleteBannerMutation.mutate(item.id);
                                },
                                onCancel() {
                                  console.log("Cancel");
                                },
                              })
                            }
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ContentValidation>
            <Pagination
              setPagination={setPage}
              page={page}
              totals={totals}
              pageCount={perPage}
            />
          </div>
        </div>
        {modalOpt.type === "new_xml_feed" && (
          <ModalXMLFeedGeneration
            onClose={closeModal}
            modalOpt={modalOpt}
          />
        )}
        {modalOpt.type === "edit_xml_feed" && (
          <ModalXMLFeedGeneration
            onClose={closeModal}
            modalOpt={modalOpt}
          />
        )}
      </ContentBlock>
    </>
  );
};

export default Feeds;
